"use client";

import React from 'react';
import { SidebarFilter, FilterOption } from '@/components/molecules/Filter';
import { SearchCard } from "@/components/molecules/SearchCard";
import PageSizeSelector from "@/components/atoms/PageSizeSelector";
import { useRouter, useSearchParams } from 'next/navigation';

// import { LoadingSpinner } from '@/components/atoms/LoadingSpinner';
import { Pagination } from '@/components/molecules/Pagination';
import { IOfertasFormativasData } from '@/services/ofertas-formativas/getOfertasData';
import CardOfertasFormativas from '@/components/molecules/cardOfertasFormativas';

interface OfertasPageClientProps {
    initialOfertas: IOfertasFormativasData;
    initialTotalItems: number;
    initialCurrentPage: number;
    initialItemsPerPage: number;
    initialFilters: FilterOption[];
    applyPrefix?: string; // URL base para candidatar (vinda do server)
}

export default function OfertasPageClient({
    initialOfertas,
    initialTotalItems,
    initialCurrentPage,
    initialItemsPerPage,
    initialFilters,
    applyPrefix,
}: OfertasPageClientProps) {
    const [showFilters, setShowFilters] = React.useState(true);
    const [showMobileFilters, setShowMobileFilters] = React.useState(false);
    // Track grid columns for ofertas
    
    const router = useRouter();
    const searchParams = useSearchParams();
    // Todos os dados necessários vêm das props SSR. Nenhum estado ou efeito é necessário.
    const totalPages = Math.ceil(initialTotalItems / initialItemsPerPage);
    const ofertas = initialOfertas?.ofertas ?? [];
    console.log('[SSR] ofertas recebidas em OfertasPageClient:', ofertas);

    

    return (
        <div className="min-h-screen bg-white">
            <div className="container mx-auto px-4 py-6 sm:py-8">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Desktop Sidebar Filter */}
                    <div className={`hidden lg:block w-full transition-all pt-6 duration-300 ${showFilters ? 'lg:w-1/4' : 'lg:w-auto'}`}>
                        <SidebarFilter
                            data={initialFilters}
                            onToggleDesktopFilter={(visible) => {
                                setShowFilters(visible);
                            }}
                        />
                        {showFilters && (
                            <div>
                                <button
                                    onClick={() => router.push('/ofertas-formativas')}
                                    className="bg-[#c3c1c0] text-[#616161] font-medium px-6 py-3 mt-8 rounded-full hover:bg-[#b3b1b0] transition-colors w-full lg:w-auto"
                                >
                                    Limpar filtros
                                </button>
                            </div>
                        )}
                    </div>
                    {/* Main Content */}
                    <div className={`w-full transition-all duration-300 ${showFilters ? 'lg:w-3/4' : 'lg:w-full'}`}>
                        {/* Search and Filter Controls */}
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6 lg:gap-12 px-2 sm:px-4 py-2 sm:py-6">
                            <div className="flex-1 max-w-full sm:max-w-[500px] lg:max-w-[710px] lg:ml-auto">
                                <SearchCard
                                    onOpenMobileFilters={() => setShowMobileFilters(true)}
                                />
                            </div>
                            <div className="flex-shrink-0 self-end sm:self-center">
                                <PageSizeSelector
                                    className="w-auto ml-auto"
                                    value={initialItemsPerPage}
                                    options={[5, 10, 20]}
                                    onChange={(newPageSize) => {
                                        const params = new URLSearchParams(searchParams.toString());
                                        params.set('pageSize', newPageSize.toString());
                                        params.set('page', '1'); // Reset to first page when changing page size
                                        router.push(`/ofertas-formativas?${params.toString()}`);
                                    }}
                                />
                            </div>
                        </div>
                        {/* Ofertas Grid */}
                        <div className="px-2 sm:px-4">
                            {ofertas.length > 0 ? (
                                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 transition-all duration-300 ${showFilters ? 'lg:grid-cols-2' : 'lg:grid-cols-4'}`}>
                                    {ofertas.map((oferta, index) => (
                                        <div key={index}>
                                            <CardOfertasFormativas
                                                id={(oferta.documentId || '').toString()}
                                                title={oferta.formacao || 'Sample Title Oferta'}
                                                level={oferta.nivel || 'Sample'}
                                                duration={oferta.duracao || '#'}
                                                period={oferta.periodoFormacao || 'Sample'}
                                                slug={(oferta as any).slug}
                                                whiteStyle={true}
                                                applyPrefix={applyPrefix}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 sm:py-12">
                                    <p className="text-gray-500 text-base sm:text-lg">Nenhuma oferta encontrada com os filtros selecionados.</p>
                                </div>
                            )}
                            {/* Paginação */}
                            {ofertas.length > 0 && totalPages > 0 && (
                                <div className="mt-8 sm:mt-12 flex justify-center z-10">
                                    <div className="rounded-full px-4 py-2">
                                        <Pagination
                                            currentPage={initialCurrentPage}
                                            totalPages={totalPages}
                                            onPageChange={(page) => {
                                                // Validação adicional
                                                if (page >= 1 && page <= totalPages && page !== initialCurrentPage) {
                                                    // Atualiza a query string SSR
                                                    const params = new URLSearchParams(window.location.search);
                                                    params.set('page', String(page));
                                                    router.push(`/ofertas-formativas?${params.toString()}`);
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Mobile Filter Modal */}
                {showMobileFilters && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center lg:hidden">
                        <div className="bg-white w-[90%] max-w-md h-[90%] max-h-[600px] rounded-lg overflow-hidden">
                            <SidebarFilter
                                data={initialFilters}
                                isMobile={true}
                                onCloseMobile={() => setShowMobileFilters(false)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}